import React from "react"
import { Switch } from "@headlessui/react"
import { FaCircleExclamation } from "react-icons/fa6"

function CenterOpening({ formData, setFormData, errors, hasWriteSettingsPermission }) {
	const days = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"]

	if (hasWriteSettingsPermission) {
		// Editable version with toggles and time inputs:
		return (
			<div>
				<h2 className="text-2xl font-semibold text-gray-900 mb-4">Center Opening Hours</h2>
				{days.map((day, index) => (
					<div key={day} className="col-span-2 sm:col-span-2 mb-6">
						<div className="flex justify-between items-center">
							<label
								htmlFor={`centerOpen.${index}`}
								className="block text-md font-bold mb-2"
							>
								{day}
							</label>
							<Switch
								checked={
									formData && formData.centerOpen
										? formData.centerOpen[index]
										: false
								}
								onChange={value => {
									setFormData(prev => {
										const newCenterOpen = [...prev.centerOpen]
										newCenterOpen[index] = value
										return { ...prev, centerOpen: newCenterOpen }
									})
								}}
								className={`${
									Boolean(formData?.centerOpen?.[index])
										? "bg-customBlue"
										: "bg-gray-200"
								} relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2`}
							>
								<span className="sr-only">Enable/Disable Day</span>
								<span
									className={`${
										(formData.centerOpen && formData.centerOpen[index]) || false
											? "translate-x-6"
											: "translate-x-1"
									} inline-block h-4 w-4 transform rounded-full bg-white transition-transform`}
								/>
							</Switch>
						</div>

						<div className="flex space-x-4 mt-1">
							<div className="flex-1">
								<label
									htmlFor={`openTime.${index}`}
									className="block text-sm font-medium text-gray-700"
								>
									Open Time
								</label>
								<div className="relative mt-2 rounded-md shadow-sm">
									<input
										type="time"
										value={
											(formData.openTime && formData.openTime[index]) || ""
										}
										onChange={e => {
											const newOpenTime = [...formData.openTime]
											newOpenTime[index] = e.target.value
											setFormData(prev => ({
												...prev,
												openTime: newOpenTime
											}))
										}}
										className={`block w-full rounded-md border-0 py-1.5 text-gray-900 placeholder:text-gray-500 focus:ring-2 focus:ring-inset sm:text-sm sm:leading-6 ${
											errors[`openTime.${index}`]
												? "pr-10 text-red-900 ring-1 ring-inset ring-red-300 placeholder:text-red-300 focus:ring-red-500"
												: "ring-1 ring-inset ring-gray-300 focus:ring-indigo-500"
										}`}
										aria-invalid={
											errors[`openTime.${index}`] ? "true" : "false"
										}
									/>
									{errors[`openTime.${index}`] && (
										<div className="pointer-events-none absolute right-3 top-1/2 transform -translate-y-1/2 flex items-center">
											<FaCircleExclamation
												aria-hidden="true"
												className="h-5 w-5 text-red-500"
											/>
										</div>
									)}
								</div>
								{errors[`openTime.${index}`] && (
									<p
										id={`openTime.${index}-error`}
										className="mt-2 text-sm text-red-600"
									>
										{errors[`openTime.${index}`]}
									</p>
								)}
							</div>

							<div className="flex-1">
								<label
									htmlFor={`closeTime.${index}`}
									className="block text-sm font-medium text-gray-700"
								>
									Close Time
								</label>
								<div className="relative mt-2 rounded-md shadow-sm">
									<input
										type="time"
										value={
											(formData.closeTime && formData.closeTime[index]) || ""
										}
										onChange={e => {
											const newCloseTime = [...formData.closeTime]
											newCloseTime[index] = e.target.value
											setFormData(prev => ({
												...prev,
												closeTime: newCloseTime
											}))
										}}
										className={`block w-full rounded-md border-0 py-1.5 text-gray-900 placeholder:text-gray-500 focus:ring-2 focus:ring-inset sm:text-sm sm:leading-6 ${
											errors[`closeTime.${index}`]
												? "pr-10 text-red-900 ring-1 ring-inset ring-red-300 placeholder:text-red-300 focus:ring-red-500"
												: "ring-1 ring-inset ring-gray-300 focus:ring-indigo-500"
										}`}
										aria-invalid={
											errors[`closeTime.${index}`] ? "true" : "false"
										}
									/>
									{errors[`closeTime.${index}`] && (
										<div className="pointer-events-none absolute right-3 top-1/2 transform -translate-y-1/2 flex items-center">
											<FaCircleExclamation
												aria-hidden="true"
												className="h-5 w-5 text-red-500"
											/>
										</div>
									)}
								</div>
								{errors[`closeTime.${index}`] && (
									<p
										id={`closeTime.${index}-error`}
										className="mt-2 text-sm text-red-600"
									>
										{errors[`closeTime.${index}`]}
									</p>
								)}
							</div>
						</div>
					</div>
				))}
			</div>
		)
	} else {
		// Read-only version that simply displays the data:
		return (
			<div>
				<h2 className="text-2xl font-semibold text-gray-900 mb-4">Center Opening Hours</h2>
				{days.map((day, index) => (
					<div key={day} className="col-span-2 sm:col-span-2 mb-6">
						<div className="flex justify-between items-center">
							<label className="block text-md font-bold mb-2">{day}</label>
							<span
								className={`text-sm ${formData.centerOpen[index] ? "text-green-700" : "text-gray-700"}`}
							>
								{formData.centerOpen[index] ? "Open" : "Closed"}
							</span>
						</div>
						<div className="flex space-x-4 mt-1">
							<div className="flex-1">
								<label className="block text-sm font-medium text-gray-700">
									Open Time
								</label>
								<span className="block w-full rounded-md py-1.5 text-gray-900 sm:text-sm">
									{formData.openTime[index] || "N/A"}
								</span>
							</div>
							<div className="flex-1">
								<label className="block text-sm font-medium text-gray-700">
									Close Time
								</label>
								<span className="block w-full rounded-md py-1.5 text-gray-900 sm:text-sm">
									{formData.closeTime[index] || "N/A"}
								</span>
							</div>
						</div>
					</div>
				))}
			</div>
		)
	}
}

export default CenterOpening
